// src/scripts/seedEchoEcho.js
// ONE-TIME seed script — creates the "Echo Echo Echo" team with full realistic data
//
// HOW TO RUN:
//   1. Add a temporary "Run Seed" button to AdminPanel.js (see bottom of this file)
//   2. Log in as keanefine@gmail.com first
//   3. Click the button — watch the browser console for progress
//   4. Remove the button and delete this file when done
//
// Creates:
//   - 1 team document
//   - 7 user documents (Keane as leader+consultant, 6 realistic members)
//   - 6 assessment responses (varied realistic scores per member)
//   - 5 meetings with full entries, sections, topics, shortcodes, quotes
//   - 5 consultant observation documents (improving trend)
//   - Full review data per meeting (section scores, topic/participant feedback)

import {
  collection, doc, setDoc, addDoc, serverTimestamp, Timestamp
} from 'firebase/firestore/lite';
import { db } from '../firebase/config';

// ─── Members ──────────────────────────────────────────────────────────────────
// geniuses / frustrations use single capital letters: W I D G E T
// (matches WorkingGenius.js which reads user.geniuses and user.frustrations as arrays of keys)

const KEANE_EMAIL = 'keanefine@gmail.com';

const MEMBERS = [
  {
    uid:  'seed-member-001',
    email: 'sarah.chen@echoecho.com',
    displayName: 'Sarah Chen',
    geniuses: ['W', 'I'],        // Wonder + Invention
    frustrations: ['T'],          // Tenacity
  },
  {
    uid:  'seed-member-002',
    email: 'marcus.webb@echoecho.com',
    displayName: 'Marcus Webb',
    geniuses: ['G', 'T'],         // Galvanizing + Tenacity
    frustrations: ['W'],           // Wonder
  },
  {
    uid:  'seed-member-003',
    email: 'priya.patel@echoecho.com',
    displayName: 'Priya Patel',
    geniuses: ['D', 'E'],          // Discernment + Enablement
    frustrations: ['G'],           // Galvanizing
  },
  {
    uid:  'seed-member-004',
    email: 'jordan.blake@echoecho.com',
    displayName: 'Jordan Blake',
    geniuses: ['I', 'D'],          // Invention + Discernment
    frustrations: ['E'],           // Enablement
  },
  {
    uid:  'seed-member-005',
    email: 'tanya.brooks@echoecho.com',
    displayName: 'Tanya Brooks',
    geniuses: ['E', 'T'],          // Enablement + Tenacity
    frustrations: ['I'],           // Invention
  },
  {
    uid:  'seed-member-006',
    email: 'daniel.ross@echoecho.com',
    displayName: 'Daniel Ross',
    geniuses: ['G', 'W'],          // Galvanizing + Wonder
    frustrations: ['D'],           // Discernment
  },
];

// ─── Assessment Profiles (1–5 per question ID) ───────────────────────────────
// 4 categories × 10 questions = 40 answers per member
// Each profile reflects a distinct personality / perspective

const PROFILES = [
  // Sarah Chen — strong behaviors, moderate process, high genius
  { sp1_q:4,sp2_q:3,sp3_q:4,sp4_q:3,sp5_q:3,sp6_q:4,sp7_q:3,sp8_q:2,sp9_q:3,sp10_q:4,
    cc1_q:4,cc2_q:3,cc3_q:3,cc4_q:4,cc5_q:4,cc6_q:3,cc7_q:4,cc8_q:3,cc9_q:3,cc10_q:4,
    hb1_q:5,hb2_q:4,hb3_q:4,hb4_q:4,hb5_q:3,hb6_q:4,hb7_q:3,hb8_q:4,hb9_q:5,hb10_q:4,
    rg1_q:4,rg2_q:5,rg3_q:3,rg4_q:3,rg5_q:4,rg6_q:3,rg7_q:3,rg8_q:4,rg9_q:3,rg10_q:4 },
  // Marcus Webb — critical of process, dissatisfied with meetings
  { sp1_q:2,sp2_q:2,sp3_q:2,sp4_q:3,sp5_q:2,sp6_q:2,sp7_q:2,sp8_q:2,sp9_q:3,sp10_q:3,
    cc1_q:3,cc2_q:2,cc3_q:2,cc4_q:3,cc5_q:3,cc6_q:2,cc7_q:3,cc8_q:3,cc9_q:2,cc10_q:3,
    hb1_q:3,hb2_q:2,hb3_q:3,hb4_q:3,hb5_q:2,hb6_q:2,hb7_q:3,hb8_q:2,hb9_q:3,hb10_q:2,
    rg1_q:3,rg2_q:3,rg3_q:4,rg4_q:5,rg5_q:3,rg6_q:3,rg7_q:2,rg8_q:3,rg9_q:2,rg10_q:3 },
  // Priya Patel — balanced, thoughtful, slightly above average
  { sp1_q:3,sp2_q:4,sp3_q:3,sp4_q:4,sp5_q:3,sp6_q:3,sp7_q:4,sp8_q:3,sp9_q:4,sp10_q:3,
    cc1_q:4,cc2_q:4,cc3_q:4,cc4_q:3,cc5_q:4,cc6_q:4,cc7_q:3,cc8_q:4,cc9_q:4,cc10_q:3,
    hb1_q:3,hb2_q:4,hb3_q:3,hb4_q:4,hb5_q:4,hb6_q:3,hb7_q:4,hb8_q:3,hb9_q:3,hb10_q:4,
    rg1_q:3,rg2_q:3,rg3_q:5,rg4_q:3,rg5_q:4,rg6_q:4,rg7_q:3,rg8_q:4,rg9_q:4,rg10_q:3 },
  // Jordan Blake — high content, lower behaviors (analytical introvert)
  { sp1_q:3,sp2_q:3,sp3_q:2,sp4_q:4,sp5_q:4,sp6_q:3,sp7_q:3,sp8_q:4,sp9_q:4,sp10_q:3,
    cc1_q:5,cc2_q:4,cc3_q:5,cc4_q:4,cc5_q:5,cc6_q:4,cc7_q:5,cc8_q:4,cc9_q:5,cc10_q:4,
    hb1_q:2,hb2_q:3,hb3_q:2,hb4_q:3,hb5_q:2,hb6_q:3,hb7_q:2,hb8_q:3,hb9_q:2,hb10_q:3,
    rg1_q:4,rg2_q:4,rg3_q:4,rg4_q:3,rg5_q:3,rg6_q:4,rg7_q:4,rg8_q:4,rg9_q:3,rg10_q:4 },
  // Tanya Brooks — across the board high scores, team champion
  { sp1_q:5,sp2_q:4,sp3_q:5,sp4_q:5,sp5_q:4,sp6_q:5,sp7_q:4,sp8_q:4,sp9_q:5,sp10_q:5,
    cc1_q:4,cc2_q:5,cc3_q:4,cc4_q:5,cc5_q:5,cc6_q:4,cc7_q:4,cc8_q:5,cc9_q:4,cc10_q:5,
    hb1_q:5,hb2_q:5,hb3_q:5,hb4_q:5,hb5_q:4,hb6_q:5,hb7_q:4,hb8_q:5,hb9_q:5,hb10_q:5,
    rg1_q:4,rg2_q:4,rg3_q:4,rg4_q:4,rg5_q:5,rg6_q:5,rg7_q:4,rg8_q:4,rg9_q:5,rg10_q:5 },
  // Daniel Ross — disengaged on process, high energy on action items
  { sp1_q:2,sp2_q:2,sp3_q:3,sp4_q:2,sp5_q:2,sp6_q:3,sp7_q:2,sp8_q:3,sp9_q:2,sp10_q:2,
    cc1_q:3,cc2_q:2,cc3_q:2,cc4_q:3,cc5_q:2,cc6_q:2,cc7_q:3,cc8_q:2,cc9_q:3,cc10_q:2,
    hb1_q:2,hb2_q:3,hb3_q:2,hb4_q:2,hb5_q:3,hb6_q:2,hb7_q:2,hb8_q:2,hb9_q:3,hb10_q:2,
    rg1_q:4,rg2_q:3,rg3_q:3,rg4_q:5,rg5_q:3,rg6_q:3,rg7_q:3,rg8_q:3,rg9_q:2,rg10_q:3 },
];

const OPEN_ENDED = [
  { strengths: 'We have a genuinely talented group and the energy when we get into real problems is unmatched.', challenges: 'Meetings can drift off topic and we lose time on things that could be handled async.', suggestions: 'Tighter agendas and a designated facilitator would make a real difference.' },
  { strengths: 'We move fast and people are willing to commit once a decision is made.', challenges: 'I feel like we jump to solutions before fully defining the problem.', suggestions: 'More time upfront exploring the question before diving into answers.' },
  { strengths: 'Psychological safety is real here — people speak up even when it is uncomfortable.', challenges: 'Sometimes the most senior voices dominate and others disengage.', suggestions: 'Structured turn-taking or round-robins for key agenda items.' },
  { strengths: 'The content of our discussions is genuinely strategic and business-relevant.', challenges: 'We sometimes leave meetings without clear ownership of next steps.', suggestions: 'A 5-minute debrief at the end of every meeting to confirm decisions and owners.' },
  { strengths: 'This team has the best meeting culture I have been part of. Healthy conflict, real laughter, and real accountability.', challenges: 'I wish we did the consultant observations more regularly — the feedback is invaluable.', suggestions: 'Monthly consultant touchpoints even if brief.' },
  { strengths: 'Strong galvanizers — this team can move mountains when motivated.', challenges: 'We underuse discernment and jump to action before we have analyzed enough.', suggestions: 'Add a mandatory devil\'s advocate role on any decision over $50k.' },
];

// ─── Meeting Entry Builder ────────────────────────────────────────────────────
// Sections use $SectionName$ syntax, topics use #TopicName#
// Genius codes use backtick format: `w `i `d `g `e `t
// Slash codes: /1 /2 /3 /4 /5 /6 /h /i /m /w /l /c

function e(speakerName, speakerId, text, offsetMs, durationMs, section = null, topic = null) {
  const base = Date.now() - 50 * 24 * 60 * 60 * 1000; // spread from 50 days ago
  return {
    id: `e-${Math.random().toString(36).slice(2, 9)}`,
    speakerName, speakerId, text,
    startTime: base + offsetMs,
    endTime:   base + offsetMs + durationMs,
    section, topic,
  };
}

function buildEntries(meetingIndex, attendees) {
  const [keane, sarah, marcus, priya, jordan, tanya, daniel] = attendees;
  const sets = [
    // ── Meeting 1: Q3 Planning Kickoff ────────────────────────────────────
    [
      e(keane.name, keane.id, '$Q3 Planning$ #Goals & Priorities# /1 Our purpose this quarter is to double down on product-led growth. `w What are we really trying to learn here?', 0, 45000, 'Q3 Planning', 'Goals & Priorities'),
      e(sarah.name, sarah.id, '/i What does success look like by end of Q3 — revenue, activation, or retention? `w', 46000, 32000, 'Q3 Planning', 'Goals & Priorities'),
      e(marcus.name, marcus.id, 'Revenue is the only honest measure. Activation can be gamed. `d /i Have we validated that with data?', 79000, 38000, 'Q3 Planning', 'Goals & Priorities'),
      e(priya.name, priya.id, 'I have the retention cohorts from last quarter. `d The drop-off at day 7 is significant — that is our real problem.', 118000, 41000, 'Q3 Planning', 'Goals & Priorities'),
      e(jordan.name, jordan.id, '"The day-7 drop is our most important problem right now." `i Has anyone done qualitative research on it yet?', 160000, 29000, 'Q3 Planning', 'Goals & Priorities'),
      e(tanya.name, tanya.id, '/h I could use some help framing the user research brief — anyone want to co-author it? `e', 190000, 25000, 'Q3 Planning', 'Goals & Priorities'),
      e(daniel.name, daniel.id, '`g Let\'s pick three bets and go. We can overthink this. /l', 216000, 22000, 'Q3 Planning', 'Goals & Priorities'),
      e(keane.name, keane.id, '#Resource Allocation# /5 The most important thing is alignment on our top bet before we leave today. `g', 239000, 35000, 'Q3 Planning', 'Resource Allocation'),
      e(marcus.name, marcus.id, '/w I have been underestimating the onboarding rework. Realistic estimate is six weeks not three. `d', 275000, 44000, 'Q3 Planning', 'Resource Allocation'),
      e(priya.name, priya.id, '/i If we shift Marcus to onboarding full time, what does that do to the API roadmap? `w', 320000, 30000, 'Q3 Planning', 'Resource Allocation'),
      e(sarah.name, sarah.id, 'The API roadmap has a two-week buffer we could absorb. `e /i Would that work Marcus?', 351000, 27000, 'Q3 Planning', 'Resource Allocation'),
      e(marcus.name, marcus.id, 'Yes — with that buffer the six-week timeline works. `t', 379000, 23000, 'Q3 Planning', 'Resource Allocation'),
      e(tanya.name, tanya.id, '/e I should have flagged the API dependency earlier. That is on me. `e', 403000, 19000, 'Q3 Planning', 'Resource Allocation'),
      e(jordan.name, jordan.id, 'Appreciated. `d Let\'s make sure dependencies are in the pre-read next time.', 423000, 24000, 'Q3 Planning', 'Resource Allocation'),
      e(keane.name, keane.id, '$Decisions$ /6 Marcus owns onboarding. Sarah owns API buffer. Priya and Tanya own research brief. `g /c Good energy — decisions were clear and owned.', 448000, 38000, 'Decisions', null),
    ],
    // ── Meeting 2: Product Review ──────────────────────────────────────────
    [
      e(keane.name, keane.id, '$Product Review$ #Onboarding Funnel# /2 We said we prioritize user trust. Does this prototype reflect that value? `w', 0, 40000, 'Product Review', 'Onboarding Funnel'),
      e(priya.name, priya.id, '/i I want to understand the step 3 design decision — why did we remove the social proof? `d', 41000, 35000, 'Product Review', 'Onboarding Funnel'),
      e(jordan.name, jordan.id, 'A/B test — version without social proof had 12% higher completion. `d "The data surprised us too."', 77000, 42000, 'Product Review', 'Onboarding Funnel'),
      e(sarah.name, sarah.id, '/i What was the sample size and did we segment by acquisition channel? `w', 120000, 31000, 'Product Review', 'Onboarding Funnel'),
      e(jordan.name, jordan.id, 'N=847, not segmented yet. `d Good call — I will pull channel cuts this week.', 152000, 28000, 'Product Review', 'Onboarding Funnel'),
      e(marcus.name, marcus.id, '`i What if we personalize step 3 by channel? Organic vs paid users need different framing.', 181000, 45000, 'Product Review', 'Onboarding Funnel'),
      e(tanya.name, tanya.id, '/l That is either a brilliant idea or a scope explosion. `w /l', 227000, 18000, 'Product Review', 'Onboarding Funnel'),
      e(priya.name, priya.id, '/i Can we scope to just two variants — organic and paid? `e', 246000, 29000, 'Product Review', 'Onboarding Funnel'),
      e(keane.name, keane.id, '#API Milestones# /5 Before we move on — is onboarding decided? Jordan, you own channel analysis by Friday? `g', 276000, 33000, 'Product Review', 'API Milestones'),
      e(jordan.name, jordan.id, 'Confirmed. Friday EOD. `t', 310000, 15000, 'Product Review', 'API Milestones'),
      e(marcus.name, marcus.id, 'API v2 auth is done. Rate limiting and docs remaining. `t Two weeks out.', 326000, 38000, 'Product Review', 'API Milestones'),
      e(daniel.name, daniel.id, '`g Let\'s ship it — done is better than perfect.', 365000, 21000, 'Product Review', 'API Milestones'),
      e(sarah.name, sarah.id, '/w I am not comfortable shipping auth without a security review. That is risk management not perfectionism. `d', 387000, 40000, 'Product Review', 'API Milestones'),
      e(marcus.name, marcus.id, 'Sarah is right. `d I will schedule the review this week. Timeline holds if review comes back clean.', 428000, 33000, 'Product Review', 'API Milestones'),
      e(keane.name, keane.id, '/c Good disagree-and-commit moment. `e /6 Marcus: security review by Wednesday. Daniel: agreed to hold the ship until review is done.', 462000, 35000, 'Product Review', null),
    ],
    // ── Meeting 3: Q3 Quarterly Check-in ──────────────────────────────────
    [
      e(keane.name, keane.id, '$Q3 Check-in$ #Scorecard# /3 We build tools that make teams smarter. Are our Q3 results showing that? `w', 0, 38000, 'Q3 Check-in', 'Scorecard'),
      e(tanya.name, tanya.id, 'DAU up 18%. Week-4 retention flat. Revenue 11% vs 15% target. `d', 39000, 35000, 'Q3 Check-in', 'Scorecard'),
      e(marcus.name, marcus.id, '/i The 11% vs 15% gap — pipeline problem or close rate problem? `d', 75000, 30000, 'Q3 Check-in', 'Scorecard'),
      e(jordan.name, jordan.id, 'Close rate is actually up. Pipeline coverage dropped. Top-of-funnel gap. `d "We have enough close rate to hit target if we fix the pipeline."', 106000, 43000, 'Q3 Check-in', 'Scorecard'),
      e(sarah.name, sarah.id, '/i Who owns top-of-funnel — marketing or product-led motion? `w', 150000, 32000, 'Q3 Check-in', 'Scorecard'),
      e(keane.name, keane.id, '#Strategic Priorities# /4 Strategic anchor is product-led growth. Pipeline gap is our most important problem. `g', 183000, 40000, 'Q3 Check-in', 'Strategic Priorities'),
      e(daniel.name, daniel.id, '`g I can run a growth sprint — two weeks, small budget. `i What is the appetite?', 224000, 36000, 'Q3 Check-in', 'Strategic Priorities'),
      e(priya.name, priya.id, '/i What would we test and what does success look like? `d', 261000, 38000, 'Q3 Check-in', 'Strategic Priorities'),
      e(daniel.name, daniel.id, 'Outreach to churned Q2 users with a re-engagement offer. `i 5% reactivation = success. `g', 300000, 35000, 'Q3 Check-in', 'Strategic Priorities'),
      e(tanya.name, tanya.id, '/e I can handle comms and segmentation if Daniel owns offer design. `e', 336000, 24000, 'Q3 Check-in', 'Strategic Priorities'),
      e(marcus.name, marcus.id, '/h Can someone walk me through how this connects to product-led growth? `w', 361000, 32000, 'Q3 Check-in', 'Strategic Priorities'),
      e(keane.name, keane.id, 'Good question. `d Reactivation data tells us what product gaps drove churn — that feeds the roadmap. `w', 394000, 40000, 'Q3 Check-in', 'Strategic Priorities'),
      e(marcus.name, marcus.id, 'Got it. `i So this is a listening exercise as much as a revenue play. Makes sense now.', 435000, 28000, 'Q3 Check-in', 'Strategic Priorities'),
      e(priya.name, priya.id, '/l "Listening exercise" — I am stealing that framing. `w', 464000, 16000, 'Q3 Check-in', 'Strategic Priorities'),
      e(keane.name, keane.id, '$Close$ /6 Daniel and Tanya own reactivation experiment — two-week timeline. Report back next check-in. `g /c Strong meeting — strategic clarity question mid-meeting was exactly right.', 481000, 38000, 'Close', null),
    ],
    // ── Meeting 4: Team Health & Genius ───────────────────────────────────
    [
      e(keane.name, keane.id, '$Team Health$ #Working Norms# /2 One of our values is radical honesty. /i What is working and what is not? `w', 0, 42000, 'Team Health', 'Working Norms'),
      e(sarah.name, sarah.id, 'What is working: safety to push back. What is not: we conflate urgency with importance — everything feels like a fire drill. `d', 43000, 48000, 'Team Health', 'Working Norms'),
      e(marcus.name, marcus.id, '/w I have been part of the noise — I escalate too quickly. Working on it. `e', 92000, 30000, 'Team Health', 'Working Norms'),
      e(tanya.name, tanya.id, 'Marcus acknowledging that publicly is exactly the culture I want to be part of. `e /l', 123000, 25000, 'Team Health', 'Working Norms'),
      e(jordan.name, jordan.id, '/i What would a clear escalation criteria look like — a 2x2 of urgency vs importance? `i', 149000, 38000, 'Team Health', 'Working Norms'),
      e(priya.name, priya.id, '`d I love this. Tier 1 = drop everything, Tier 2 = address this week, Tier 3 = next meeting. Trust people to self-sort.', 188000, 42000, 'Team Health', 'Working Norms'),
      e(daniel.name, daniel.id, '`g Let\'s build this. I can have a draft by Thursday. `t', 231000, 22000, 'Team Health', 'Working Norms'),
      e(keane.name, keane.id, '#Genius & Energy# /i How well are we using each person\'s genius? `w Who feels like they are in their frustration zone?', 254000, 40000, 'Team Health', 'Genius & Energy'),
      e(marcus.name, marcus.id, '/w Honestly — I spend too much time in Enablement and not enough in Galvanizing. It drains me. `w', 295000, 35000, 'Team Health', 'Genius & Energy'),
      e(sarah.name, sarah.id, '`w I feel that. I am a natural Wonder but meetings move so fast I rarely get to explore before we jump to solutions.', 331000, 38000, 'Team Health', 'Genius & Energy'),
      e(priya.name, priya.id, '/i Could we structure the first 5 minutes of any agenda item as pure Wonder — just curiosity, no solutions? `w `i', 370000, 36000, 'Team Health', 'Genius & Energy'),
      e(tanya.name, tanya.id, '"The 5-minute Wonder window" — I love it. `e Let\'s trial it. `g', 407000, 23000, 'Team Health', 'Genius & Energy'),
      e(jordan.name, jordan.id, '`d What metrics would tell us it is working? Participation breadth in the first 5 minutes?', 431000, 31000, 'Team Health', 'Genius & Energy'),
      e(keane.name, keane.id, '$Commitments$ /6 Daniel owns escalation criteria by Thursday. Wonder window starts next meeting. Keane checks in on energy in 30 days. /c Best meeting of the quarter. Real vulnerability, real output. `e', 463000, 45000, 'Commitments', null),
    ],
    // ── Meeting 5: Q3 Retro & Q4 Planning ────────────────────────────────
    [
      e(keane.name, keane.id, '$Q3 Retro$ #Wins# /1 We exist to make teams better. Did we get better this quarter? `w Let\'s start with wins.', 0, 38000, 'Q3 Retro', 'Wins'),
      e(tanya.name, tanya.id, 'Reactivation experiment hit 7.2% — beat our 5% goal. `t "This team executes when we commit."', 39000, 32000, 'Q3 Retro', 'Wins'),
      e(daniel.name, daniel.id, '`g Proud of how fast we moved. Two weeks from idea to results. `t', 72000, 24000, 'Q3 Retro', 'Wins'),
      e(priya.name, priya.id, 'Escalation tiers are actually being used. 30% drop in Slack pings that should have been async. `e', 97000, 36000, 'Q3 Retro', 'Wins'),
      e(marcus.name, marcus.id, '`d API v2 shipped and security review found zero critical issues. Proud of that. `t', 134000, 30000, 'Q3 Retro', 'Wins'),
      e(sarah.name, sarah.id, 'The Wonder window has been transformative. I feel heard in a way I did not before. `w', 165000, 35000, 'Q3 Retro', 'Wins'),
      e(jordan.name, jordan.id, 'Channel-segmented data informed two product decisions — direct output of one meeting conversation. `d `i', 201000, 38000, 'Q3 Retro', 'Wins'),
      e(keane.name, keane.id, '#Learnings# /i What would we do differently? What patterns do we want to break in Q4? `w', 240000, 38000, 'Q3 Retro', 'Learnings'),
      e(marcus.name, marcus.id, '/w I still escalate too fast. Tier system helps but the instinct remains. `d I want a 24-hour rule before tagging anything Tier 1.', 279000, 40000, 'Q3 Retro', 'Learnings'),
      e(priya.name, priya.id, '`d We made two decisions without enough data this quarter. They worked out but we got lucky. Discernment before action is non-negotiable on big bets.', 320000, 44000, 'Q3 Retro', 'Learnings'),
      e(tanya.name, tanya.id, '/i What if we require a 48-hour pre-read for any decision over $10k? `w', 365000, 32000, 'Q3 Retro', 'Learnings'),
      e(jordan.name, jordan.id, '`i I like the 48-hour rule. `d It forces the decision-maker to articulate trade-offs before the room weighs in.', 398000, 35000, 'Q3 Retro', 'Learnings'),
      e(daniel.name, daniel.id, '/e I called two Tier 1 escalations that were really Tier 2. I owe the team time back. `e', 434000, 28000, 'Q3 Retro', 'Learnings'),
      e(sarah.name, sarah.id, 'Daniel\'s self-awareness is what makes this team different. "We have a team where people admit when they are wrong." `w', 463000, 38000, 'Q3 Retro', 'Learnings'),
      e(keane.name, keane.id, '$Q4 Commitments$ /4 Q4 strategic anchors: product-led growth, team health as competitive advantage, discernment before action. `g /5 Most important: ship retention feature before end of October. /6 Priya owns 48-hour pre-read proposal. Marcus owns 24-hour Tier 1 rule. Full team owns the Wonder window. /c This team is genuinely exceptional. The growth this quarter has been remarkable. `e', 502000, 55000, 'Q4 Commitments', null),
    ],
  ];
  return sets[meetingIndex];
}

// ─── Consultant Observations (improving trend across 5 meetings) ──────────────

const OBS_SETS = [
  // Meeting 1 — moderate (27/40 observed)
  { sp1_q:true,sp2_q:false,sp3_q:true,sp4_q:true,sp5_q:false,sp6_q:true,sp7_q:false,sp8_q:false,sp9_q:true,sp10_q:true,
    cc1_q:true,cc2_q:false,cc3_q:true,cc4_q:true,cc5_q:true,cc6_q:false,cc7_q:true,cc8_q:false,cc9_q:true,cc10_q:true,
    hb1_q:true,hb2_q:true,hb3_q:true,hb4_q:true,hb5_q:false,hb6_q:true,hb7_q:false,hb8_q:true,hb9_q:true,hb10_q:false,
    rg1_q:true,rg2_q:true,rg3_q:true,rg4_q:true,rg5_q:false,rg6_q:true,rg7_q:false,rg8_q:true,rg9_q:false,rg10_q:true },
  // Meeting 2 — improving (31/40)
  { sp1_q:true,sp2_q:true,sp3_q:true,sp4_q:true,sp5_q:false,sp6_q:true,sp7_q:true,sp8_q:false,sp9_q:true,sp10_q:true,
    cc1_q:true,cc2_q:true,cc3_q:true,cc4_q:true,cc5_q:true,cc6_q:true,cc7_q:true,cc8_q:false,cc9_q:true,cc10_q:true,
    hb1_q:true,hb2_q:true,hb3_q:true,hb4_q:true,hb5_q:true,hb6_q:true,hb7_q:false,hb8_q:true,hb9_q:true,hb10_q:true,
    rg1_q:true,rg2_q:true,rg3_q:true,rg4_q:true,rg5_q:true,rg6_q:false,rg7_q:true,rg8_q:true,rg9_q:false,rg10_q:true },
  // Meeting 3 — strong (35/40)
  { sp1_q:true,sp2_q:true,sp3_q:true,sp4_q:true,sp5_q:true,sp6_q:true,sp7_q:true,sp8_q:false,sp9_q:true,sp10_q:true,
    cc1_q:true,cc2_q:true,cc3_q:true,cc4_q:true,cc5_q:true,cc6_q:true,cc7_q:true,cc8_q:true,cc9_q:true,cc10_q:true,
    hb1_q:true,hb2_q:true,hb3_q:true,hb4_q:true,hb5_q:true,hb6_q:true,hb7_q:true,hb8_q:true,hb9_q:true,hb10_q:false,
    rg1_q:true,rg2_q:true,rg3_q:true,rg4_q:true,rg5_q:true,rg6_q:true,rg7_q:false,rg8_q:true,rg9_q:true,rg10_q:true },
  // Meeting 4 — excellent (38/40)
  { sp1_q:true,sp2_q:true,sp3_q:true,sp4_q:true,sp5_q:true,sp6_q:true,sp7_q:true,sp8_q:true,sp9_q:true,sp10_q:true,
    cc1_q:true,cc2_q:true,cc3_q:true,cc4_q:true,cc5_q:true,cc6_q:true,cc7_q:true,cc8_q:true,cc9_q:false,cc10_q:true,
    hb1_q:true,hb2_q:true,hb3_q:true,hb4_q:true,hb5_q:true,hb6_q:true,hb7_q:true,hb8_q:true,hb9_q:true,hb10_q:true,
    rg1_q:true,rg2_q:true,rg3_q:true,rg4_q:true,rg5_q:true,rg6_q:true,rg7_q:true,rg8_q:true,rg9_q:true,rg10_q:true },
  // Meeting 5 — near-perfect (39/40)
  { sp1_q:true,sp2_q:true,sp3_q:true,sp4_q:true,sp5_q:true,sp6_q:true,sp7_q:true,sp8_q:true,sp9_q:true,sp10_q:true,
    cc1_q:true,cc2_q:true,cc3_q:true,cc4_q:true,cc5_q:true,cc6_q:true,cc7_q:true,cc8_q:true,cc9_q:true,cc10_q:true,
    hb1_q:true,hb2_q:true,hb3_q:true,hb4_q:true,hb5_q:true,hb6_q:true,hb7_q:true,hb8_q:true,hb9_q:true,hb10_q:true,
    rg1_q:true,rg2_q:true,rg3_q:true,rg4_q:true,rg5_q:true,rg6_q:true,rg7_q:false,rg8_q:true,rg9_q:true,rg10_q:true },
];

// ─── Meeting Review Data (sectionScores, sectionFeedback, topicFeedback, participantFeedback) ──

const REVIEWS = [
  {
    sectionScores: { lightning:3, clarity:2, scoreboard:3, realtime:4, strategic:3, decisions:4, cascading:3 },
    sectionFeedback: {
      lightning: 'Updates were prepared but some ran long. Marcus\'s timeline flag was well-handled.',
      clarity: 'Organizational clarity was referenced but not fully leveraged in the resource decision.',
      scoreboard: 'No formal scoreboard — this was a planning session.',
      realtime: 'Strong real-time problem solving on the resource allocation question.',
      strategic: 'Good depth on Q3 goals. Day-7 retention framing was excellent.',
      decisions: 'Decisions named at end but ownership could be clearer mid-meeting.',
      cascading: 'Cascading messages identified but not formalized.',
    },
    topicFeedback: { 0: 'Good problem definition. Wonder phase was present.', 1: 'Solid decision on resource reallocation. Good disagree-and-commit.' },
    topicDecisions: { 0: 'Decided', 1: 'Decided' },
    participantFeedback: {
      'Keane Fine': 'Strong facilitation. Good consulting comments. Could push for more voices earlier in discussions.',
      'Sarah Chen': 'Excellent inquiry-to-advocacy balance. The day-7 retention question was the best of the meeting.',
      'Marcus Webb': 'Real vulnerability admitting the timeline gap. That set the tone for honest conversation.',
      'Priya Patel': 'Strong discernment throughout. Brought data at exactly the right moment.',
      'Jordan Blake': 'Good strategic framing. The day-7 quote anchored the whole conversation.',
      'Tanya Brooks': 'Proactive and enabling. Offer to co-author was a great Enablement moment.',
      'Daniel Ross': 'Galvanizing energy is an asset. Needs to slow down on the Wonder phase before jumping to solutions.',
    },
    selectedQuotes: { 'prod-0': true },
  },
  {
    sectionScores: { lightning:4, clarity:3, scoreboard:2, realtime:4, strategic:4, decisions:5, cascading:3 },
    sectionFeedback: {
      lightning: 'Well-prepared, good brevity throughout.',
      clarity: 'Values were invoked naturally in the security debate — good instinct.',
      scoreboard: 'Metrics referenced but not formally reviewed.',
      realtime: 'Excellent real-time problem solving on the security review question.',
      strategic: 'Personalization discussion showed real strategic thinking.',
      decisions: 'Best decisions clarity so far. Ownership was explicit and confirmed on the spot.',
      cascading: 'Some cascading messages identified informally.',
    },
    topicFeedback: { 0: 'A/B test data used well. Good follow-up questions from Sarah.', 1: 'Security debate was a healthy moment. Disagree-and-commit practiced well.' },
    topicDecisions: { 0: 'Tabled', 1: 'Decided' },
    participantFeedback: {
      'Keane Fine': 'The consulting comment on the disagree-and-commit moment was well-timed and reinforcing.',
      'Sarah Chen': 'Standing firm on the security review was exactly right. Good courageous advocacy.',
      'Marcus Webb': 'Owned the security gap immediately. Clear growth from meeting 1.',
      'Priya Patel': 'Good scoping instinct on personalization — kept the idea manageable.',
      'Jordan Blake': 'Data-driven throughout. The A/B test quote anchored the discussion well.',
      'Tanya Brooks': 'Levity at exactly the right moment. Scope explosion comment was perfect.',
      'Daniel Ross': 'Strong galvanizing energy but needs to check the impulse to ship before adequate review.',
    },
    selectedQuotes: { 'prod-0': true },
  },
  {
    sectionScores: { lightning:4, clarity:4, scoreboard:5, realtime:4, strategic:5, decisions:4, cascading:3 },
    sectionFeedback: {
      lightning: 'Clean and efficient.',
      clarity: 'Strategic anchors directly referenced and genuinely useful.',
      scoreboard: 'Best scoreboard review of the quarter. Numbers were current and team engaged directly.',
      realtime: 'Good problem identification on the pipeline gap.',
      strategic: 'Excellent conversation. Reactivation experiment idea was well-developed in real time.',
      decisions: 'Clear decisions, explicit ownership.',
      cascading: 'Cascading messages not formally addressed.',
    },
    topicFeedback: { 0: 'Metrics discussion was sharp. Strong data usage.', 1: 'Strategic conversation was the highlight of the quarter so far.' },
    topicDecisions: { 0: 'Decided', 1: 'Decided' },
    participantFeedback: {
      'Keane Fine': 'Strategic anchor reference mid-meeting was excellent — redirected the conversation perfectly.',
      'Sarah Chen': 'The top-of-funnel ownership question was incisive. Got to the root issue fast.',
      'Marcus Webb': 'Connecting the reactivation experiment to the roadmap was a genuine insight.',
      'Priya Patel': 'Discernment was strong throughout. Good pushback on experiment scope.',
      'Jordan Blake': 'Close rate vs pipeline coverage framing was the clearest strategic insight of the quarter.',
      'Tanya Brooks': 'Ready to execute before the decision was even finalized — great Enablement.',
      'Daniel Ross': 'Infectious energy on the growth sprint idea. Good self-questioning on the PLG connection.',
    },
    selectedQuotes: { 'prod-0': true },
  },
  {
    sectionScores: { lightning:4, clarity:4, scoreboard:3, realtime:5, strategic:4, decisions:5, cascading:4 },
    sectionFeedback: {
      lightning: 'Good preparation. Honest updates.',
      clarity: 'Values were the explicit starting point — well set up by Keane.',
      scoreboard: 'Not a scoreboard-focused meeting by design.',
      realtime: 'Best real-time problem solving of the quarter. The escalation tier discussion was exceptional.',
      strategic: 'Genius conversation was genuinely strategic — connecting individual energy to team design.',
      decisions: 'Clear, owned, and publicly committed.',
      cascading: 'Wonder window and escalation tiers explicitly cascaded to team norms.',
    },
    topicFeedback: { 0: 'Fire drill pattern naming was brave and accurate.', 1: 'Genius conversation was the most personally meaningful of any meeting so far.' },
    topicDecisions: { 0: 'Decided', 1: 'Decided' },
    participantFeedback: {
      'Keane Fine': 'Best facilitation of the quarter. The genius question was right one at the right time.',
      'Sarah Chen': 'The Wonder frustration disclosure was vulnerable and important. Set the tone for everyone.',
      'Marcus Webb': 'Self-awareness about escalation was remarkable. Public accountability at its best.',
      'Priya Patel': 'The Wonder window proposal was elegant and actionable. Strong Discernment-to-Invention arc.',
      'Jordan Blake': 'Operational thinking on Wonder window metrics was exactly right.',
      'Tanya Brooks': '"The 5-minute Wonder window" — soundbite of the quarter.',
      'Daniel Ross': 'Fastest anyone has committed to a deliverable. Energy in service of the team.',
    },
    selectedQuotes: { 'prod-0': true },
  },
  {
    sectionScores: { lightning:5, clarity:5, scoreboard:5, realtime:5, strategic:5, decisions:5, cascading:5 },
    sectionFeedback: {
      lightning: 'Exemplary. Wins were specific, owned, and celebrated.',
      clarity: 'Purpose and values were lived — not just referenced.',
      scoreboard: 'Results were specific and connected to decisions made earlier in the quarter.',
      realtime: 'The retrospective itself was the agenda and it ran perfectly.',
      strategic: 'Q4 anchors were clear, compelling, and connected to Q3 learnings.',
      decisions: 'Every commitment named, owned, and confirmed. Best decisions clarity of the year.',
      cascading: 'Pre-read proposal and Tier 1 rule explicitly cascaded. Wonder window re-committed.',
    },
    topicFeedback: { 0: 'Wins celebrated without becoming self-congratulatory.', 1: 'Learnings were honest and actionable — a rare combination.' },
    topicDecisions: { 0: 'Decided', 1: 'Decided' },
    participantFeedback: {
      'Keane Fine': 'Most complete meeting I have observed. Full marks across the board. Q4 strategic anchor framing was masterful.',
      'Sarah Chen': 'The Wonder window impact statement was genuine and moving. This is what culture change looks like.',
      'Marcus Webb': 'The 24-hour Tier 1 self-commitment was remarkable accountability. This sets the bar for the team.',
      'Priya Patel': 'The 48-hour pre-read proposal is the right structural intervention. Excellent discernment.',
      'Jordan Blake': 'Channel-segmented data driving product decisions — this is what inquiry-to-impact looks like.',
      'Tanya Brooks': 'The execution results speak for themselves. 7.2% reactivation on a two-week experiment.',
      'Daniel Ross': 'Mistake acknowledgment done with grace and humor. That is a skill. Keep developing it.',
    },
    selectedQuotes: { 'prod-0': true, 'prod-1': true },
  },
];

// ─── Main Export ──────────────────────────────────────────────────────────────

export async function seedEchoEcho(keaneUid) {
  if (!keaneUid) {
    console.error('❌ Pass your real Firebase UID: seedEchoEcho(uid)');
    console.error('   Find it: Firebase Console → Authentication → Users');
    console.error('   Or run: firebase.auth().currentUser.uid in the browser console');
    return { success: false };
  }

  console.log('🌱 Starting Echo Echo Echo seed...');
  console.log('   Using UID:', keaneUid);

  try {
    // 1. Create team document
    const teamRef = doc(collection(db, 'teams'), 'echo-echo-echo-team');
    await setDoc(teamRef, {
      name: 'Echo Echo Echo',
      leaderEmail: KEANE_EMAIL,
      consultantEmails: [KEANE_EMAIL],
      memberEmails: MEMBERS.map(m => m.email),
      status: 'active',
      createdAt: serverTimestamp(),
    });
    const teamId = teamRef.id;
    console.log('✅ Team created:', teamId);

    // 2. Keane's user document (team_leader + consultant)
    await setDoc(doc(db, 'users', keaneUid), {
      email: KEANE_EMAIL,
      displayName: 'Keane Fine',
      role: 'team_leader',
      teamId,
      // WorkingGenius.js reads these fields (single capital letters)
      geniuses: ['W', 'G'],
      frustrations: ['T'],
      createdAt: serverTimestamp(),
    }, { merge: true }); // merge so we don't overwrite an existing account
    console.log('✅ Keane user doc created/updated');

    // 3. Six team members
    for (const m of MEMBERS) {
      await setDoc(doc(db, 'users', m.uid), {
        email: m.email,
        displayName: m.displayName,
        role: 'team_member',
        teamId,
        geniuses: m.geniuses,
        frustrations: m.frustrations,
        createdAt: serverTimestamp(),
      });
    }
    console.log('✅ 6 team member user docs created');

    // 4. Assessment responses
    for (let i = 0; i < MEMBERS.length; i++) {
      await addDoc(collection(db, 'responses'), {
        userId: MEMBERS[i].uid,
        teamId,
        role: 'team_member',
        answers: PROFILES[i],
        openEnded: OPEN_ENDED[i],
        submittedAt: serverTimestamp(),
      });
    }
    console.log('✅ 6 assessment responses created');

    // 5. Meetings + observations + reviews
    const meetingNames = [
      'Q3 Planning Kickoff',
      'Product Review — Sprint 7',
      'Q3 Quarterly Check-in',
      'Team Health & Genius Conversation',
      'Q3 Retrospective & Q4 Planning',
    ];

    const attendees = [
      { id: keaneUid, name: 'Keane Fine' },
      ...MEMBERS.map(m => ({ id: m.uid, name: m.displayName })),
    ];

    for (let i = 0; i < 5; i++) {
      const daysAgo = (5 - i) * 10; // meetings spread over last 50 days
      const meetingDate = Timestamp.fromDate(
        new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000)
      );
      const entries = buildEntries(i, attendees);
      const review = REVIEWS[i];

      const meetingRef = await addDoc(collection(db, 'meetings'), {
        name: meetingNames[i],
        teamId,
        teamName: 'Echo Echo Echo',
        consultantId: keaneUid,
        consultantName: 'Keane Fine',
        attendees,
        entries,
        status: 'reviewed',
        meetingDate,
        meetingStartTime: meetingDate.toMillis(),
        meetingEndTime:   meetingDate.toMillis() + 55 * 60 * 1000,
        totalDuration:    55 * 60 * 1000,
        // Review data — consumed by MeetingReport.js and MeetingReview.js
        sectionScores:       review.sectionScores,
        sectionFeedback:     review.sectionFeedback,
        topicFeedback:       review.topicFeedback,
        topicDecisions:      review.topicDecisions,
        participantFeedback: review.participantFeedback,
        selectedQuotes:      review.selectedQuotes,
        reviewedAt:          serverTimestamp(),
        reviewedBy:          keaneUid,
        createdAt:           serverTimestamp(),
      });
      console.log(`✅ Meeting ${i + 1} created: ${meetingRef.id}`);

      // Consultant observation — consumed by ConsultantAssessment.js + PerceivedVsObserved.js
      const obs = OBS_SETS[i];
      await addDoc(collection(db, 'consultantObservations'), {
        meetingId:        meetingRef.id,
        teamId,
        consultantId:     keaneUid,
        consultantEmail:  KEANE_EMAIL,
        observed:         obs,
        questionIds:      Object.keys(obs),
        observedCount:    Object.values(obs).filter(Boolean).length,
        totalQuestions:   40,
        submittedAt:      serverTimestamp(),
      });
      console.log(`✅ Observation ${i + 1} created`);
    }

    console.log('');
    console.log('🎉 Seed complete! Echo Echo Echo is ready.');
    console.log('   Team ID:', teamId);
    return { success: true, teamId };

  } catch (err) {
    console.error('❌ Seed failed:', err);
    return { success: false, error: err.message };
  }
}

/*
══════════════════════════════════════════════════════════════════════
  ADD THIS TEMPORARILY TO AdminPanel.js to run the seed:

  import { seedEchoEcho } from '../scripts/seedEchoEcho';
  import { useAuth } from '../context/AuthContext';

  // Inside the component:
  const { currentUser } = useAuth();

  // Inside the JSX (add near the top of your admin panel):

  <div style={{ padding:16, background:'#fff8e1', border:'1px solid #f4a261', borderRadius:8, marginBottom:24 }}>
    <strong>🌱 Seed Data (one-time)</strong>
    <p style={{ fontSize:'0.85rem', margin:'8px 0' }}>
      Creates Echo Echo Echo team with full realistic data. Run once only.
    </p>
    <button
      className="btn btn-primary"
      onClick={async () => {
        const result = await seedEchoEcho(currentUser.uid);
        alert(result.success
          ? '✅ Seed complete! Check the console for details.'
          : '❌ Seed failed: ' + result.error);
      }}
    >
      Run Seed
    </button>
  </div>

══════════════════════════════════════════════════════════════════════
*/
